import { datadogRum } from '@datadog/browser-rum';
import { BackspaceOutlined, Check } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useRouter } from '@tanstack/react-router';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { PORTER_PASSWORD_LENGTH } from '../const/porter-password-length';
import DialButton from '../../../components/DialButton';
import LoginErrorModal from '../../../components/LoginErrorDialog';
import OnClickModal from '../../../components/OnClickDialog';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext';
import { useBandwidthContext } from '../../../contexts/BandwidthContext';

const PasswordDial = ({ username }: { username: string }) => {
  const { loginPorter, loginError, loading } = useAuthenticationContext();
  const { status } = useBandwidthContext();
  const router = useRouter();
  const [password, setPassword] = useState('');
  const [isClicked, setIsClicked] = useState(false);

  const isUnstableConnection = status !== 'online';

  const onLogin = useCallback(async () => {
    if (isUnstableConnection) {
      setIsClicked(true);
      return;
    }
    await loginPorter(username, password);
    await router.invalidate();
  }, [username, password, router, loginPorter, isUnstableConnection]);

  useEffect(() => {
    if (loginError) {
      setPassword('');
    }
  }, [loginError, router]);

  const onDial = (value: string) => {
    datadogRum.addAction('dial-button-click');
    setPassword((prev) => {
      if (prev.length < 4) {
        return `${prev}${value}`;
      }

      return prev;
    });
  };

  const handleBackspace = () => {
    setPassword((prev) => prev.slice(0, -1));
  };

  const handleClose = () => {
    setIsClicked(false);
  };

  return (
    <>
      <OnClickModal
        isClicked={isClicked}
        type="error"
        message={t('errors.unstableConnection')}
        handleClose={handleClose}
      />
      <LoginErrorModal loginError={loginError} />
      <Stack spacing={10} height="100%">
        <Stack height="100%" alignItems="center" justifyContent="center" spacing={10}>
          <Typography minHeight={20}>{password.split('').map(() => '*')}</Typography>
          <Box display="grid" gap={8} gridTemplateColumns="repeat(3, auto)" gridTemplateRows="repeat(4, auto)">
            {Array.from(new Array(9)).map((_, i) => (
              <DialButton key={i} value={`${i + 1}`} onClick={onDial} disabled={loading} />
            ))}
            <IconButton
              sx={{ margin: 'auto' }}
              variant="filled"
              size="extra-large"
              color="primary"
              onClick={handleBackspace}
              disabled={loading || password.length === 0}
            >
              <BackspaceOutlined />
            </IconButton>
            <DialButton value="0" onClick={onDial} disabled={loading} />

            {loading ? (
              <Box sx={{ margin: 'auto' }}>
                <CircularProgress />
              </Box>
            ) : (
              <IconButton
                sx={{ margin: 'auto' }}
                variant="filled"
                size="extra-large"
                color="primary"
                onClick={onLogin}
                disabled={password.length < PORTER_PASSWORD_LENGTH}
              >
                <Check />
              </IconButton>
            )}
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default PasswordDial;
